import React, { Dispatch, SetStateAction, useState } from "react"
import {
  CartContainer,
  CartItems,
  Label
} from "../../Order/ManageOrder/RefundOrder/Cart/Cart.styled"
import Totals from "../../Order/ManageOrder/RefundOrder/Cart/Totals"
import * as graphql from "lib/types/generated/graphql-types"
import {
  CustomReasonCode,
  Maybe,
  Order,
  OrderHistory,
  OrderOperationSearchHit
} from "lib/types/generated/graphql-types"
import Reason from "../../Order/Updates/Reason"
import { ArrowIcon, OrderLabel, OrderTotal } from "../Shared.styled"
import { Link } from "react-router-dom"
import OperationOrderLine from "./OperationOrderLine"
import OperationShippingFee from "./OperationShippingFee"
import OperationGiftCardProduct from "./OperationGiftCardProduct"
import Money from "../../Money"
import DineroFactory from "dinero.js"
import StartOperation from "./StartOperation"
import { useMutation } from "@apollo/client"
import { handleErrorMessages } from "helpers/errors"
import START_REFUND from "../../../graphql/mutations/order/refunds/StartRefund"
import PendingRefund from "../../Order/ManageOrder/RefundOrder/PendingRefund"
import FailedRefund from "../../Order/ManageOrder/RefundOrder/FailedRefund"

type Props = {
  operation: OrderOperationSearchHit
  order: Order
  orderHistory: OrderHistory
  getCustomCode: (code: Maybe<string> | undefined) => CustomReasonCode
  setShow: Dispatch<SetStateAction<boolean>>
  refetchOrder: () => void
  setCreatedOperations: Dispatch<SetStateAction<OrderOperationSearchHit[]>>
  setFailedOperations: Dispatch<SetStateAction<OrderOperationSearchHit[]>>
}

const RefundAction = ({
  operation,
  order,
  refetchOrder,
  orderHistory,
  getCustomCode,
  setShow,
  setCreatedOperations,
  setFailedOperations
}: Props) => {
  const [giftCardAction, setGiftCardAction] = useState<graphql.InputActionGiftCard>({
    actionType: graphql.InputActionType.Auto
  })
  const [bonusAction, setBonusAction] = useState<graphql.InputActionBonus>({
    actionType: graphql.InputActionType.Auto
  })
  const [paymentAction, setPaymentAction] = useState<graphql.InputActionPaymentRefund>({
    actionType: graphql.InputActionType.Auto
  })

  const [startRefund, { loading }] = useMutation(START_REFUND, {
    onCompleted: () => {
      setShow(false)
      setCreatedOperations((prev) => prev.filter((hit) => hit.id !== operation.id))
    },
    onError: (error) => {
      handleErrorMessages(error)
    }
  })

  const startOrderRefund = () => {
    startRefund({
      variables: {
        refundId: refund?.id,
        input: {
          ...(refund?.bonus && { bonus: bonusAction }),
          ...(refund?.giftCards && refund?.giftCards?.length > 0 && { giftCard: giftCardAction }),
          payment: paymentAction
        }
      }
    })
  }

  const refund = order.Refunds.find((refund) => refund.id === operation.id)

  const getOrderLine = (refundLineId: string) => {
    return order?.orderLines?.find((orderLine) => orderLine.id === refundLineId)
  }

  const getShippingFee = (refundFeeId: string) => {
    return order?.shippingFees?.find((fee) => fee.id === refundFeeId)
  }

  const isRefundFailed = refund?.failed && !refund.completed
  const isRefundPending = refund?.started && !refund.completed && !refund?.failed

  if (isRefundPending)
    return (
      <PendingRefund pendingRefund={refund} orderRefetch={refetchOrder} orderLoading={loading} />
    )

  if (isRefundFailed)
    return (
      <>
        <OrderLabel>
          Order: {order.reference}{" "}
          <Link to={`/orders/${order.id}`} target="_blank">
            <ArrowIcon />
          </Link>
        </OrderLabel>
        <FailedRefund
          orderId={order.id}
          failedRefund={refund}
          currencyCode={order.currencyCode}
          orderHistory={orderHistory.history}
          setFailedOperations={setFailedOperations}
        />
      </>
    )

  return (
    <>
      <CartContainer key={refund?.id}>
        <OrderLabel>
          Order: {order.reference}{" "}
          <Link to={`/orders/manage-order/${order.id}`} target="_blank">
            <ArrowIcon />
          </Link>
        </OrderLabel>

        <CartItems>
          {refund?.orderLines.map((ol) => (
            <OperationOrderLine
              key={ol.orderLineId}
              orderLine={getOrderLine(ol.orderLineId)}
              orderQuantity={ol.quantity}
            />
          ))}
          {refund?.shippingFees?.map((shippingFee) => (
            <OperationShippingFee
              shippingFee={getShippingFee(shippingFee.shippingFeeId)}
              key={shippingFee.shippingFeeId}
            />
          ))}
          {refund?.giftCardProducts?.map((giftCardProduct) => (
            <OperationGiftCardProduct
              giftCardProduct={giftCardProduct}
              key={giftCardProduct.giftCardProductId}
            />
          ))}
          <Reason reason={refund?.reason} getCustomCode={getCustomCode} />
          <OrderTotal>
            <div>Order Total</div>{" "}
            <div>
              <Money
                amount={order.totals.grandTotal}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </div>
          </OrderTotal>
          <Label>To be refunded: </Label>
          {refund?.totals && (
            <Totals
              totals={refund?.totals}
              currencyCode={order?.currencyCode}
              refundFee={refund?.fee}
            />
          )}
        </CartItems>
        <StartOperation
          operation={refund}
          actionState={{
            giftCardAction,
            setGiftCardAction,
            bonusAction,
            setBonusAction,
            paymentAction,
            setPaymentAction
          }}
          startOperation={startOrderRefund}
          loading={loading}
        />
      </CartContainer>
    </>
  )
}

export default RefundAction
